(() => {
  'use strict';

  if (customElements.get('golden-ticket-widget')) return;

  class GoldenTicketWidget extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.returnFocusTarget = null;
      this.openFrame = null;
      this.openGeneration = 0;
      this.handleOpenClick = this.open.bind(this);
      this.handleCloseClick = () => this.close();
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-golden-ticket-trigger]');
      this.closeButton = this.querySelector('[data-golden-ticket-close]');
      this.continueButton = this.querySelector('[data-golden-ticket-continue]');
      this.backdrop = this.querySelector('[data-golden-ticket-backdrop]');
      this.panel = this.querySelector('#GoldenTicketPanel');

      if (!this.trigger || !this.closeButton || !this.backdrop || !this.panel) return;
      this.trigger.addEventListener('click', this.handleOpenClick);
      this.closeButton.addEventListener('click', this.handleCloseClick);
      this.continueButton?.addEventListener('click', this.handleCloseClick);
      this.backdrop.addEventListener('click', this.handleCloseClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.handleOpenClick);
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.continueButton?.removeEventListener('click', this.handleCloseClick);
      this.backdrop?.removeEventListener('click', this.handleCloseClick);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      if (this.isOpen) this.close({ restoreFocus: false });
      else this.cancelPendingFocus();
    }

    open() {
      if (this.isOpen || !this.isConnected || !this.trigger || !this.closeButton || !this.backdrop || !this.panel) return;
      this.cancelPendingFocus();
      this.isOpen = true;
      this.returnFocusTarget = this.trigger;
      this.classList.add('golden-ticket-widget--open');
      document.body.classList.add('golden-ticket-widget-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');

      const openingGeneration = ++this.openGeneration;
      const focusFrame = requestAnimationFrame(() => {
        if (this.openFrame === focusFrame) this.openFrame = null;
        if (!this.isOpen || !this.isConnected || openingGeneration !== this.openGeneration) return;
        this.closeButton.focus({ preventScroll: true });
      });
      this.openFrame = focusFrame;
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.cancelPendingFocus();
      this.isOpen = false;
      this.classList.remove('golden-ticket-widget--open');
      document.body.classList.remove('golden-ticket-widget-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');

      if (restoreFocus) {
        const focusTarget = this.returnFocusTarget instanceof HTMLElement && this.returnFocusTarget === this.trigger
          ? this.returnFocusTarget
          : this.trigger;
        const restoreGeneration = this.openGeneration;
        const focusFrame = requestAnimationFrame(() => {
          if (this.openFrame === focusFrame) this.openFrame = null;
          if (this.isOpen || !this.isConnected || restoreGeneration !== this.openGeneration) return;
          focusTarget?.focus({ preventScroll: true });
        });
        this.openFrame = focusFrame;
      }
      this.returnFocusTarget = null;
    }

    cancelPendingFocus() {
      if (this.openFrame !== null) {
        cancelAnimationFrame(this.openFrame);
        this.openFrame = null;
      }
      this.openGeneration += 1;
    }

    handleDocumentKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') this.keepFocusInside(event);
    }

    keepFocusInside(event) {
      const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const focusable = [...this.panel.querySelectorAll(selector)].filter((element) => !element.hasAttribute('hidden'));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('golden-ticket-widget', GoldenTicketWidget);
})();
